import React, { useEffect, useMemo, useState } from "react";
import { DashboardData, MonthlySummary } from "./types/dashboard";
import { Wallet, Package, AlertCircle, BarChart3, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { useFormatAmount } from "../home/hooks/useFormatAmount";

interface StatsOverviewProps {
  dashboardData: DashboardData;
  selectedMonth: string;
  selectedYear: number;
  onMonthChange: (month: string) => void;
  onYearChange: (year: number) => void;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const StatsOverview: React.FC<StatsOverviewProps> = ({ dashboardData, selectedMonth, selectedYear, onMonthChange, onYearChange }) => {
  type BusinessType = "Laundry" | "Restaurant" | "Saloon" | "Car Wash" ;
  const [businessType, setBusinessType] = useState<BusinessType | "">("");

  const navigate = useNavigate();
  const { user } = useUser();
  const formatAmount = useFormatAmount();
  // Get the full created date, not just year
  const userCreatedDate = useMemo(() => {
    // Check both possible locations for createdOn
    if (user?.createdOn) {
      return new Date(user.createdOn);
    }
    return new Date(2020, 0, 1); // fallback to Jan 1, 2020
  }, [user]);

  const userCreatedYear = userCreatedDate.getFullYear();
  const userCreatedMonth = userCreatedDate.getMonth(); // 0-11

  // Updated buildMonthYearOptions to accept creation month
  const monthYearOptions = useMemo(() => {
    const options: {
      label: string;
      month: string;
      year: number;
    }[] = [];

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    for (let year = currentYear; year >= userCreatedYear; year--) {
      // Determine start month for this year
      let startMonth = 0; // Default: January
      if (year === userCreatedYear) {
        startMonth = userCreatedMonth; // Start from creation month for first year
      }

      // Determine end month for this year
      let endMonth = 11; // Default: December
      if (year === currentYear) {
        endMonth = currentMonth; // Don't show future months for current year
      }

      for (let m = endMonth; m >= startMonth; m--) {
        options.push({
          label: `${year} - ${MONTHS[m]}`,
          month: MONTHS[m],
          year,
        });
      }
    }

    return options;
  }, [userCreatedYear, userCreatedMonth]);

  const monthlySummary: MonthlySummary[] = dashboardData?.calculate_monthly_summary || [];

  const ordersSummary: MonthlySummary[] = dashboardData?.calculate_monthly_summary || [];

  const currentMonthData = monthlySummary.find((m) => m.month === selectedMonth && m.year === selectedYear);

  const currentMonthOrders = ordersSummary.find((m) => m.month === selectedMonth && m.year === selectedYear);

  const totalRevenue = formatAmount(currentMonthData?.total_received || 0);
  const activeOrders = currentMonthOrders?.order_count || 0;
  const totalSales = formatAmount(currentMonthOrders?.total_sales || 0);

  const outstanding = formatAmount(dashboardData?.overall_outstanding_balance || 0);
  useEffect(() => {
    const storedType = sessionStorage.getItem("business_type") as BusinessType | null;
    if (storedType) setBusinessType(storedType);
  }, []);


  const mainStats = [
    {
      title: "Revenue",
      value: `QAR ${totalRevenue.toLocaleString()}`,
      icon: <Wallet className="w-6 h-6 text-emerald-600" />,
      gradient: "from-emerald-50 to-white",
      borderColor: "border-emerald-500",
    },
    {
      title: "Orders",
      value: activeOrders.toString(),
      icon: <Package className="w-6 h-6 text-blue-600" />,
      gradient: "from-blue-50 to-white",
      borderColor: "border-blue-500",
    },
    {
      title: "Total Sales",
      value: `QAR ${totalSales.toLocaleString()}`,
      icon: <BarChart3 className="w-6 h-6 text-amber-600" />,
      gradient: "from-amber-50 to-white",
      borderColor: "border-amber-500",
    },
  ];

  const outstandingStat =
    businessType && businessType !== "Restaurant"
      ? {
          title: "Outstanding Balance",
          value: `QAR ${outstanding.toLocaleString()}`,
          icon: <AlertCircle className="w-6 h-6 text-red-600" />,
          gradient: "from-red-50 to-white",
          borderColor: "border-red-500",
        }
      : null;

  return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className={`bg-white rounded-2xl shadow-md p-3 ${outstandingStat ? "lg:col-span-3" : "lg:col-span-4"}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-gray-900">Monthly Overview</h2>

            <select
              value={`${selectedYear}-${selectedMonth}`}
              onChange={(e) => {
                const [year, month] = e.target.value.split("-");
                onYearChange(Number(year));
                onMonthChange(month);
              }}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm min-w-[140px]"
            >
              {monthYearOptions.map((opt) => (
                <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mainStats.map((stat, i) => (
              <div key={i} className={`relative bg-gray-200 rounded-2xl border-l-8 ${stat.borderColor}`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-50`} />
                <div className="relative z-10 p-5 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600 uppercase">{stat.title}</p>
                    <p className="text-xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                  {stat.icon}
                </div>
              </div>
            ))}
          </div>
        </div>

        {outstandingStat && (
          <div
            className="bg-white rounded-2xl shadow-md p-3 lg:col-span-1 h-full flex flex-col"
            // onClick={() => navigate("/v1/ledger")}
          >
            <h2 className="text-base font-bold text-gray-900 mb-2">Overall Outstanding</h2>

            <div className={`relative bg-white rounded-2xl border-l-8 ${outstandingStat.borderColor} flex flex-col h-full`}>
              <div className={`absolute inset-0 bg-gradient-to-br ${outstandingStat.gradient} opacity-50`} />

              <div className="relative z-10 px-5 pt-3 pb-2 flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600 uppercase">{outstandingStat.title}</p>
                  <p className="text-xl font-semibold text-gray-900">{outstandingStat.value}</p>
                </div>
                {outstandingStat.icon}
              </div>

              {/* CTA pinned to bottom */}
              <div
                className="relative z-10 flex items-center gap-2 mt-auto p-1.5 px-6 border-t border-gray-100 cursor-pointer hover:-translate-y-1 transition-all duration-200 ease-out group"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/v1/ledger");
                }}
              >
                <span className="text-sm text-blue-600 font-medium group-hover:text-blue-700 transition-colors">View Ledger</span>
                <ChevronRight className="w-4 h-4 text-blue-600 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default StatsOverview;
